import React, { Suspense, useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { set, get, onValue } from "firebase/database";
import { useTexture, Loader, PerspectiveCamera } from "@react-three/drei";
import Avatar from "./components/avatar";
import ErrorScreen from "./components/error/ErrorScreen";
import {
  auth,
  httpsCallable,
  functions,
  signInWithCustomToken,
  onAuthStateChanged,
  onChildAdded,
  query,
  limitToLast,
  orderByChild,
  analytics,
} from "./firebase";
import { useMount } from "react-use";

import ReactAudioPlayer from "react-audio-player";

import TutorVoiceStatus from "./tutor/tutorVoiceStatus";

import { dbRefs } from "./db";
import { ErrorBoundary } from "react-error-boundary";
import ModelLoadingStatus from "./tutor/modelLoadingStatus";
import { logEvent } from "firebase/analytics";

const STYLES = {
  area: { position: "absolute", bottom: "10px", left: "10px", zIndex: 500 },
  text: {
    margin: "0px",
    width: "300px",
    padding: "5px",
    background: "none",
    color: "#ffffff",
    fontSize: "1.2em",
    border: "none",
  },
  speak: {
    padding: "10px",
    marginTop: "5px",
    display: "block",
    color: "#FFFFFF",
    background: "#222222",
    border: "None",
  },
  area2: { position: "absolute", top: "5px", right: "15px", zIndex: 500 },
  label: { color: "#777777", fontSize: "0.8em" },
};

let thinkStartTime = null;

const useAuth = () => {
  const [user, setUser] = useState(null);
  const urlParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        console.log("Already signed in:", JSON.stringify(currentUser));
        setUser(currentUser);
        const userId = urlParams.get("userId");
        if (userId && currentUser.uid !== userId) {
          console.log("User ID mismatch, signing out...");
          auth.signOut();
        }
      } else {
        console.log("Not signed in, trying to sign in...");
        const idToken = urlParams.get("token");
        if (idToken) {
          const generateCustomToken = httpsCallable(
            functions,
            "generateCustomToken"
          );
          generateCustomToken(idToken).then((result) => {
            signInWithCustomToken(auth, result.data);
          });
        }
      }
    });

    return () => unsubscribe();
  }, []);

  return user;
};

function App() {
  const user = useAuth();
  const audioPlayer = useRef();

  const [speak, setSpeak] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [text, setText] = useState("");
  const [audioSource, setAudioSource] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [initTime] = useState(Date.now());
  const [voiceStatusRef, setVoiceStatusRef] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const showInput = urlParams.get("showInput");
    if (showInput) {
      setShowInput(true);
    }
  }, []);

  useMount(() => {
    document.body.style.overflow = "hidden";
  });

  useEffect(() => {
    if (!user) return;
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("userId");
    const roomId = urlParams.get("roomId");
    if (!userId || !roomId) return;

    const lastMessageQuery = query(
      dbRefs.messagesRef(userId, roomId),
      orderByChild("createdAt"),
      limitToLast(1)
    );
    const voiceStatusRef = dbRefs.chatStateVoiceStatusRef(userId, roomId);
    setVoiceStatusRef(voiceStatusRef);
    const onMessageAdded = onChildAdded(lastMessageQuery, async (snapshot) => {
      const voiceStatus = await get(voiceStatusRef);
      console.log("Voice status:", voiceStatus.val());
      if (voiceStatus.val() === TutorVoiceStatus.LIMIT_REACHED) return;
      const message = snapshot.val();
      const allowSay = message["alwaysSay"] || message["createdAt"] >= initTime;
      console.log("New message:", JSON.stringify(message), allowSay);
      if (!allowSay) return;
      if (message["sender"] === "tutor") {
        setText(message["text"]);
        thinkStartTime = Date.now();
        setSpeak(true);
        set(voiceStatusRef, TutorVoiceStatus.THINKING);
      }
    });

    const onVoiceStatusChanged = onValue(voiceStatusRef, (snapshot) => {
      if (snapshot.val() === TutorVoiceStatus.IDLE) {
        endPlay();
      }
    });

    return () => {
      onMessageAdded();
      onVoiceStatusChanged();
    };
  }, [user]);

  function modelLoadingFailed(error, info) {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get("userId");
    const roomId = urlParams.get("roomId");
    if (!userId || !roomId) return;
    const modelLoadingStatusRef = dbRefs.chatStateModelLoadingStatusRef(
      userId,
      roomId
    );
    set(modelLoadingStatusRef, ModelLoadingStatus.ERROR);
  }

  function endPlay() {
    const playerElement = audioPlayer.current.audioEl.current;
    if (playerElement) {
      playerElement.pause();
      new Promise((resolve) => setTimeout(resolve, 100)).then(playerEnded);
    }
  }

  function playerEnded(e) {
    setAudioSource(null);
    setSpeak(false);
    setPlaying(false);
  }

  useEffect(() => {
    if (!speak && voiceStatusRef) {
      set(voiceStatusRef, TutorVoiceStatus.IDLE);
    }
  }, [speak]);

  // Player is read
  function playerReady(e) {
    if (voiceStatusRef) {
      set(voiceStatusRef, TutorVoiceStatus.SPEAKING);
    }
    audioPlayer.current.audioEl.current.play();
    setPlaying(true);
    if (thinkStartTime) {
      const thinkDuration = Date.now() - thinkStartTime;
      logEvent(analytics, "tutorStartSpeaking", {
        duration: (thinkDuration / 1000).toFixed(1),
      });
      thinkStartTime = null;
    }
  }

  return (
    <div className="full">
      {showInput && (
        <div style={STYLES.area}>
          <textarea
            rows={4}
            type="text"
            style={STYLES.text}
            value={text}
            onChange={(e) => setText(e.target.value.substring(0, 200))}
          />
          <button onClick={() => setSpeak(true)} style={STYLES.speak}>
            {" "}
            {speak ? "Running..." : "Speak"}
          </button>
        </div>
      )}
      <ReactAudioPlayer
        src={audioSource}
        ref={audioPlayer}
        autoPlay={true}
        onEnded={playerEnded}
        onCanPlayThrough={playerReady}
      />
      <ErrorBoundary fallback={<ErrorScreen />} onError={modelLoadingFailed}>
        <Canvas
          dpr={2}
          onCreated={(ctx) => {
            ctx.gl.physicallyCorrectLights = true;
          }}
          style={{ background: "#ffffff" }}
        >
          <PerspectiveCamera makeDefault fov={50} position={[0, 1.65, 1]} />

          <Suspense fallback={null}>
            <Bg />
          </Suspense>

          <Suspense fallback={null}>
            <Avatar
              avatar_url="https://cdn.fluenza.ai/model.glb"
              speak={speak}
              setSpeak={setSpeak}
              text={text}
              setAudioSource={setAudioSource}
              playing={playing}
              user={user}
            />
          </Suspense>
        </Canvas>
      </ErrorBoundary>
      <Loader
        dataInterpolation={(p) => {
          return `Loading the model, please wait…`;
        }}
        dataStyles={{
          left: "50%",
          transform: "translateX(-50%)",
          width: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          whiteSpace: "normal",
        }}
      />
    </div>
  );
}

function Bg() {
  const texture = useTexture("/images/office-background.jpg");

  return (
    <mesh position={[0, 1.5, -0.5]} scale={[3, 2, 2]}>
      <planeBufferGeometry />
      <meshBasicMaterial map={texture} />
    </mesh>
  );
}

export default App;
